/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { api, mutatingPost, put, type ApiResult } from "./client";
import { PAGE_SIZE } from "./registry";
import { withSort, type SortChoice } from "../lib/sorting";
import type { CareTeamKind, ChildCareTeam } from "./careTeam";

/** Mirror of nexus AccountResponses.AccountSummary. */
export type AccountSummary = {
  id: number;
  email: string | null;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  legalFirstName: string | null;
  phone: string | null;
  status: string;
  lockedOut: boolean;
  roles: string[];
  familyId: number | null;
  lastActiveAt: string | null;
  createdAt: string | null;
  careTeamKind: CareTeamKind | null;
};

/** Mirror of nexus AccountResponses.SearchResponse. */
export type AccountSearchPage = {
  items: AccountSummary[];
  page: number;
  pageSize: number;
  total: number;
};

export type AccountProfile = {
  id: number;
  email: string | null;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  legalFirstName: string | null;
  nickname: string | null;
  phone: string | null;
  dob: string | null;
  gender: string | null;
  timeZone: string | null;
  theme: string | null;
  avatarUrl: string | null;
  familyId: number | null;
  ehrUserId: string | null;
  groupName: string | null;
  activeClient: boolean | null;
  lastActiveAt: string | null;
};

export type Lockout = {
  failedLoginCount: number;
  lastFailedLoginAt: string | null;
  lockedOut: boolean;
};

export type EmailVerification = { verified: boolean; verifiedAt: string | null };

export type FamilyMember = {
  id: number;
  firstName: string | null;
  lastName: string | null;
  status: string;
  lockedOut: boolean;
  roles: string[];
};

export type MirrorLink = { id: string; externalId: string | null; status: string | null };

export type CoParentInvite = {
  id: number;
  email: string | null;
  programKey: string | null;
  createdAt: string | null;
};

export type CareTeamInvite = {
  id: number;
  inviteeEmail: string | null;
  clientName: string | null;
  accountType: string;
  careProviderId: number | null;
  careCoordinatorId: number | null;
  linked: boolean;
  createdAt: string | null;
};

export type AccountInvites = { coParent: CoParentInvite[]; careTeam: CareTeamInvite[] };

export type Sessions = { refreshTokens: number; legacySessionTokens: number; total: number };

export type Audit = {
  createdAt: string | null;
  createdBy: number | null;
  updatedAt: string | null;
  updatedBy: number | null;
};

export type HealthieSyncField = "email" | "phoneE164" | "firstName" | "lastName" | "legalFirstName";

/** A field whose newest change Healthie refused, with Healthie's own reason when it gave one. */
export type HealthieRefusal = { field: HealthieSyncField; reason: string | null; failedAt: string };

/** Mirror of nexus AccountResponses.AccountDetailsResponse. */
export type AccountDetails = {
  profile: AccountProfile;
  status: string;
  lockout: Lockout;
  emailVerification: EmailVerification;
  roles: string[];
  familyMembers: FamilyMember[];
  patients: MirrorLink[];
  staff: MirrorLink[];
  invites: AccountInvites;
  sessions: Sessions;
  audit: Audit;
  /** Whether the messaging setting applies: any care-team role, Support included. */
  careTeam: boolean;
  /** Null for an account no care team can name, Support included. */
  careTeamKind: CareTeamKind | null;
  canReceiveMessages: boolean | null;
  /** Null while Healthie holds every change made here. */
  healthieSync: { fields: HealthieRefusal[] } | null;
};

/** Mirror of nexus AccountResponses.ChangeView (one audit-trail row). */
export type AccountChange = {
  id: string;
  field: string;
  oldValue: string | null;
  newValue: string | null;
  changedAt: string;
  changedBy: number | null;
};

export type EditAccountResult = { account: AccountSummary; emailVerificationReset: boolean };

export type UnlockResult = { id: number; failedLoginCount: number; status: string };

export const ACCOUNT_STATUSES = [
  "UNVERIFIED",
  "PENDING_ACTIVATION",
  "ACTIVE",
  "DISABLED",
  "BLOCKED",
  "CONSENT_REQUIRED",
] as const;

export const ROLE_NAMES = [
  "Admin",
  "Support",
  "CareCoordinator",
  "Therapist",
  "ParentCoach",
  "Parent",
  "Child",
  "StandaloneClient",
  "BillingSpecialist",
] as const;

/**
 * Mirror of nexus AccountAdminService.ALLOWED_TRANSITIONS — the status dropdown offers only what
 * the server accepts. PENDING_ACTIVATION, UNVERIFIED, and CONSENT_REQUIRED are system-driven and
 * never a manual target.
 */
export const ALLOWED_STATUS_TRANSITIONS: Record<string, string[]> = {
  ACTIVE: ["DISABLED", "BLOCKED"],
  DISABLED: ["ACTIVE", "BLOCKED"],
  BLOCKED: ["ACTIVE", "DISABLED"],
  UNVERIFIED: ["DISABLED", "BLOCKED"],
  PENDING_ACTIVATION: ["DISABLED", "BLOCKED"],
  CONSENT_REQUIRED: ["DISABLED", "BLOCKED"],
};

export type AccountScope = "clients" | "internal" | "care_team";

export const CLIENT_ROLES = ["Parent", "Child", "StandaloneClient"] as const;
/** The roles that run families' care, care teams included. */
export const CARE_STAFF_ROLES = ["Admin", "Support", "CareCoordinator"] as const;
export const CARE_TEAM_ROLES = [
  "CareCoordinator",
  "Therapist",
  "ParentCoach",
  "BillingSpecialist",
] as const;
export const INTERNAL_ROLES = ["Admin", "Support", ...CARE_TEAM_ROLES] as const;

export type SearchQuery = {
  q?: string;
  status?: string;
  role?: string;
  scope?: AccountScope;
  page?: number;
  pageSize?: number;
  sort?: SortChoice;
};

export function searchAccounts(query: SearchQuery): Promise<ApiResult<AccountSearchPage>> {
  const params = new URLSearchParams();
  if (query.q !== undefined && query.q !== "") params.set("q", query.q);
  if (query.status !== undefined && query.status !== "") params.set("status", query.status);
  if (query.role !== undefined && query.role !== "") params.set("role", query.role);
  if (query.scope !== undefined) params.set("scope", query.scope);
  params.set("page", String(query.page ?? 1));
  params.set("pageSize", String(query.pageSize ?? PAGE_SIZE));
  if (query.sort !== undefined) {
    withSort(params, query.sort);
  }
  return api<AccountSearchPage>(`/v1/accounts/?${params.toString()}`);
}


export type FamilyMemberDetail = {
  id: number;
  firstName: string | null;
  lastName: string | null;
  nickname: string | null;
  email: string | null;
  username: string | null;
  phone: string | null;
  status: string;
  lockedOut: boolean;
  roles: string[];
};

export type FamilyPatient = {
  id: string;
  externalId: string | null;
  userId: number | null;
  status: string;
  primaryInsurance: string | null;
  primaryInsuranceName: string | null;
  tags: string[];
  memberId: string | null;
  createdAt: string;
};

export type FamilyDetails = {
  familyId: number;
  state: string | null;
  createdAt: string;
  members: FamilyMemberDetail[];
  patients: FamilyPatient[];
  invites: { id: number; email: string; createdAt: string }[];
  careTeam: ChildCareTeam[];
};

export function familyDetails(id: string): Promise<ApiResult<FamilyDetails>> {
  return api<FamilyDetails>(`/v1/accounts/families/${id}`);
}

export function accountDetails(id: string): Promise<ApiResult<AccountDetails>> {
  return api<AccountDetails>(`/v1/accounts/${id}`);
}

export function accountChanges(
  id: string,
  limit = 20,
): Promise<ApiResult<{ items: AccountChange[] }>> {
  return api<{ items: AccountChange[] }>(`/v1/accounts/${id}/changes?limit=${limit}`);
}

export type EditAccountBody = {
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  legalFirstName?: string;
};

export function editAccount(
  id: string,
  body: EditAccountBody,
): Promise<ApiResult<EditAccountResult>> {
  return api<EditAccountResult>(`/v1/accounts/${id}`, put(body));
}

export function setAccountStatus(
  id: string,
  status: string,
): Promise<ApiResult<AccountSummary>> {
  return api<AccountSummary>(`/v1/accounts/${id}/status`, put({ status }));
}

/** Refused (400) for an account without a care-team role. */
export function setCanReceiveMessages(
  id: string,
  canReceiveMessages: boolean,
): Promise<ApiResult<AccountSummary>> {
  return api<AccountSummary>(`/v1/accounts/${id}/messaging`, put({ canReceiveMessages }));
}

export function setAccountRoles(
  id: string,
  roles: string[],
): Promise<ApiResult<AccountSummary>> {
  return api<AccountSummary>(`/v1/accounts/${id}/roles`, put({ roles }));
}

export function sendPasswordReset(id: string): Promise<ApiResult<{ message: string }>> {
  return api<{ message: string }>(
    `/v1/accounts/${id}/actions/send-password-reset`,
    mutatingPost({}),
  );
}

export function sendVerification(id: string): Promise<ApiResult<{ message: string }>> {
  return api<{ message: string }>(
    `/v1/accounts/${id}/actions/send-verification`,
    mutatingPost({}),
  );
}

/** Refused (409) when Healthie holds every change, (400) for an account without a Healthie link. */
export function retryHealthieSync(id: string): Promise<ApiResult<{ message: string }>> {
  return api<{ message: string }>(
    `/v1/accounts/${id}/actions/retry-healthie-sync`,
    mutatingPost({}),
  );
}

export function unlockAccount(id: string): Promise<ApiResult<UnlockResult>> {
  return api<UnlockResult>(`/v1/accounts/${id}/actions/unlock`, mutatingPost({}));
}

export function moveToFamily(
  id: string,
  familyId: number,
  note: string,
): Promise<ApiResult<AccountSummary>> {
  return api<AccountSummary>(
    `/v1/accounts/${id}/actions/move-to-family`,
    mutatingPost({ familyId, note }),
  );
}

export function resendCareTeamInvite(inviteId: number): Promise<ApiResult<{ message: string }>> {
  return api<{ message: string }>(`/v1/invites/therapist/${inviteId}/resend`, mutatingPost({}));
}

export function reinviteCoParent(
  email: string,
  programKey: string | null,
): Promise<ApiResult<{ invited: boolean }>> {
  return api<{ invited: boolean }>("/v1/invites/co-parent", mutatingPost({ email, programKey }));
}
